import { generarBloquesTurno } from 'schemas';

interface TurnoRango {
  hora_inicio: string;
  hora_fin: string;
}

interface RegistroRango {
  hora_inicio_bloque: string;
  hora_fin_bloque: string;
  estado?: string | null;
}

export interface HuecoCaptura {
  inicio: string;
  fin: string;
  /** true cuando la celda ya tiene al menos un tramo válido. */
  esParcial: boolean;
}

const SEGUNDOS_DIA = 24 * 60 * 60;

function segundosDia(hora: string): number {
  const [hh, mm, ss = 0] = hora.split(':').map(Number);
  return (hh ?? 0) * 3600 + (mm ?? 0) * 60 + (ss ?? 0);
}

function duracionSegundos(registro: RegistroRango): number {
  const inicio = segundosDia(registro.hora_inicio_bloque);
  let fin = segundosDia(registro.hora_fin_bloque);
  if (fin <= inicio) fin += SEGUNDOS_DIA;
  return fin - inicio;
}

function posicionDesdeTurno(hora: string, inicioTurno: number): number {
  return (segundosDia(hora) - inicioTurno + SEGUNDOS_DIA) % SEGUNDOS_DIA;
}

function formatearHora(segundosAbsolutos: number): string {
  const total = ((Math.round(segundosAbsolutos) % SEGUNDOS_DIA) + SEGUNDOS_DIA) % SEGUNDOS_DIA;
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  const hhmm = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  return ss === 0 ? hhmm : `${hhmm}:${String(ss).padStart(2, '0')}`;
}

/**
 * Calcula la cobertura faltante real de cada celda del turno.
 *
 * Se usa la unión de todos los registros válidos: una captura multi-SKU puede
 * tener varias filas físicas y una celda parcialmente corregida puede dejar
 * uno o más huecos. Los rangos se linealizan desde el inicio del turno para
 * que T3 conserve el orden 22:00…00:00…06:00.
 */
export function calcularHuecosCaptura(
  turno: TurnoRango,
  registros: RegistroRango[],
): HuecoCaptura[] {
  const inicioTurno = segundosDia(turno.hora_inicio);
  const rangos = registros
    .filter((registro) => registro.estado !== 'invalidado')
    .map((registro) => {
      const inicio = posicionDesdeTurno(registro.hora_inicio_bloque, inicioTurno);
      return { inicio, fin: inicio + duracionSegundos(registro) };
    });

  let inicioCelda = 0;
  const huecos: HuecoCaptura[] = [];
  for (const celda of generarBloquesTurno({
    ...turno,
    cruza_medianoche: turno.hora_fin < turno.hora_inicio,
  })) {
    const finCelda = inicioCelda + celda.duracionMinutos * 60;
    const coberturas = rangos
      .map((rango) => ({
        inicio: Math.max(inicioCelda, rango.inicio),
        fin: Math.min(finCelda, rango.fin),
      }))
      .filter((rango) => rango.fin > rango.inicio)
      .sort((a, b) => a.inicio - b.inicio);

    let cursor = inicioCelda;
    for (const cobertura of coberturas) {
      if (cobertura.inicio > cursor) {
        huecos.push({
          inicio: formatearHora(inicioTurno + cursor),
          fin: formatearHora(inicioTurno + cobertura.inicio),
          esParcial: true,
        });
      }
      cursor = Math.max(cursor, cobertura.fin);
    }
    if (cursor < finCelda) {
      huecos.push({
        inicio: formatearHora(inicioTurno + cursor),
        fin: formatearHora(inicioTurno + finCelda),
        esParcial: coberturas.length > 0,
      });
    }
    inicioCelda = finCelda;
  }

  return huecos;
}
